import { relations, sql } from 'drizzle-orm'
import { boolean, decimal, index, integer, pgTable, text, timestamp, uuid, varchar } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'
import z from 'zod/v4'

import { user } from '../../auth'
import { VoucherScopeEnum, VoucherTypeEnum } from '../enums'
import { VoucherToOrganization } from './organization'
import { VoucherToPrice } from './price'

export const Voucher = pgTable(
  'voucher',
  {
    id: uuid().primaryKey().defaultRandom(),
    name: varchar({ length: 256 }).notNull(),
    type: VoucherTypeEnum().notNull(),
    /**
     * Which checkout this voucher belongs to. Defaults to `CHECKOUT` so every existing voucher
     * keeps meaning exactly what it meant before this column existed.
     */
    scope: VoucherScopeEnum().notNull().default('CHECKOUT'),
    value: decimal().notNull(),
    usage: integer().notNull().default(0),
    limit: integer(),
    minimumTransaction: integer(),
    maximumValue: integer(),
    startedAt: timestamp({ mode: 'date', withTimezone: true }).notNull(),
    endedAt: timestamp({ mode: 'date', withTimezone: true }).notNull(),
    createdByUserId: text().references(() => user.id, { onDelete: 'set null' }),
    isActive: boolean().notNull().default(true),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    // Index for voucher type
    index('idx_voucher_type').on(t.type),

    // Composite index for date range queries (covers both startedAt and endedAt lookups)
    index('idx_voucher_date_range').on(t.startedAt, t.endedAt),
  ],
)

export const voucherRelations = relations(Voucher, ({ one, many }) => ({
  createdByUser: one(user, {
    fields: [Voucher.createdByUserId],
    references: [user.id],
  }),

  voucherToPrices: many(VoucherToPrice),

  voucherToOrganizations: many(VoucherToOrganization),
}))

export const InsertVoucherSchema = createInsertSchema(Voucher, {
  name: z.string().min(1).max(256),
  type: z.enum(VoucherTypeEnum.enumValues),
  scope: z.enum(VoucherScopeEnum.enumValues).default('CHECKOUT'),
  value: z.string().regex(/^\d+(\.\d+)?$/, 'Must be a valid decimal number'),
  usage: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().optional(),
  minimumTransaction: z.number().int().nonnegative().optional(),
  maximumValue: z.number().int().nonnegative().optional(),
  startedAt: z.date(),
  endedAt: z.date(),
  createdByUserId: z.string().optional(),
  isActive: z.boolean().default(true),
})
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .superRefine((data, ctx) => {
    // Validate that endedAt is after startedAt
    if (data.endedAt <= data.startedAt) {
      ctx.addIssue({
        code: 'custom',
        message: 'End date must be after start date',
        path: ['endedAt'],
      })
    }

    // Validate that usage doesn't exceed limit if limit is set
    if (data.limit !== undefined && data.usage > data.limit) {
      ctx.addIssue({
        code: 'custom',
        message: 'Usage cannot exceed limit',
        path: ['usage'],
      })
    }
  })

export const UpdateVoucherSchema = createUpdateSchema(Voucher, {
  name: z.string().min(1).max(256),
  type: z.enum(VoucherTypeEnum.enumValues),
  scope: z.enum(VoucherScopeEnum.enumValues),
  value: z.string().regex(/^\d+(\.\d+)?$/, 'Must be a valid decimal number'),
  limit: z.number().int().positive(),
  minimumTransaction: z.number().int().nonnegative(),
  maximumValue: z.number().int().nonnegative(),
  startedAt: z.date(),
  endedAt: z.date(),
  isActive: z.boolean(),
})
  .omit({
    id: true,
    // `usage` now counts redemptions (incremented on payment) — an edit must never be able to
    // set it, so it is not part of the update payload at all.
    usage: true,
    createdByUserId: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial()
  .superRefine((data, ctx) => {
    // Validate that endedAt is after startedAt if both are provided
    if (data.startedAt && data.endedAt && data.endedAt <= data.startedAt) {
      ctx.addIssue({
        code: 'custom',
        message: 'End date must be after start date',
        path: ['endedAt'],
      })
    }
  })

export const SelectVoucherSchema = createSelectSchema(Voucher, {
  id: z.uuid(),
  name: z.string(),
  type: z.enum(VoucherTypeEnum.enumValues),
  value: z.string(),
  usage: z.number().int(),
  limit: z.number().int().nullable(),
  minimumTransaction: z.number().int().nullable(),
  maximumValue: z.number().int().nullable(),
  startedAt: z.date(),
  endedAt: z.date(),
  createdByUserId: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
})

export type InsertVoucher = typeof Voucher.$inferInsert
export type UpdateVoucher = z.infer<typeof UpdateVoucherSchema>
export type SelectVoucher = typeof Voucher.$inferSelect
